import { useState } from "react";
import { Aperture, ArrowRight, BookOpen, Camera, FilmStrip, GridFour, Images, Sparkle, SquaresFour, Stack, TextT, Wind } from "@phosphor-icons/react";
import type { TemplateId } from "../domain/project";
import { AlbumPreview } from "./AlbumPreview";
import { Reveal } from "./HomeMotion";
import { getShowcaseDocument, SHOWCASE_COLLECTIONS, SHOWCASE_ENTRIES, SHOWCASE_URLS, type DemoCollection } from "./home-showcase-data";

const icons = {
  book: BookOpen,
  paper: TextT,
  timeline: FilmStrip,
  gallery: Images,
  cinema: FilmStrip,
  deck: Stack,
  focus: Camera,
  scrapbook: SquaresFour,
  accordion: GridFour,
  drift: Wind,
  circular: Aperture,
  depth: Sparkle,
};

interface HomeTemplateShowcaseProps {
  onOpenTemplate?: (template: TemplateId, collection: DemoCollection) => void;
}

export function HomeTemplateShowcase({ onOpenTemplate }: HomeTemplateShowcaseProps) {
  const [activeTemplate, setActiveTemplate] = useState<TemplateId>("book");
  const [activeCollection, setActiveCollection] = useState<DemoCollection>("landscape");
  const active = SHOWCASE_ENTRIES.find((entry) => entry.id === activeTemplate) ?? SHOWCASE_ENTRIES[0];
  const ActiveIcon = icons[active.id];
  const collection = SHOWCASE_COLLECTIONS[activeCollection];
  const previewDocument = getShowcaseDocument(activeTemplate, activeCollection);

  return (
    <section className="home-showcase" id="reading-modes" aria-labelledby="reading-modes-title">
      <Reveal className="home-showcase-intro">
        <div>
          <span className="eyebrow">十二种阅读方式</span>
          <h2 id="reading-modes-title">同一段记忆，<br /><em>可以有十二种打开方式。</em></h2>
        </div>
        <p>先把照片和文字放在一起，再选择最像你的那一种节奏。这里的案例来自真实照片，不是抽象的占位图。</p>
      </Reveal>
      <Reveal className="home-showcase-layout" delay={80}>
        <div className="home-showcase-sidebar">
          <div className="home-showcase-collections" aria-label="选择案例类型">
            {(Object.entries(SHOWCASE_COLLECTIONS) as [DemoCollection, typeof collection][]).map(([id, item]) => (
              <button key={id} type="button" className="home-showcase-collection" aria-pressed={id === activeCollection} onClick={() => setActiveCollection(id)}>
                <span>{item.label}</span><small>{id === "people" ? "PORTRAITS" : "LANDSCAPES"}</small>
              </button>
            ))}
          </div>
          <div className="home-showcase-tabs" aria-label="选择阅读方式">
            {SHOWCASE_ENTRIES.map((entry) => {
              const Icon = icons[entry.id];
              const selected = entry.id === activeTemplate;
              return (
                <button
                  className="home-showcase-tab"
                  type="button"
                  aria-pressed={selected}
                  key={entry.id}
                  onClick={() => setActiveTemplate(entry.id)}
                >
                  <Icon size={17} weight={selected ? "fill" : "regular"} />
                  <span>{entry.name}</span>
                  <small>{String(SHOWCASE_ENTRIES.indexOf(entry) + 1).padStart(2, "0")}</small>
                </button>
              );
            })}
          </div>
          <div className="home-showcase-detail" aria-live="polite">
            <div className="home-showcase-detail-mark"><ActiveIcon size={18} />{active.eyebrow}</div>
            <h3>{active.name}</h3>
            <p>{active.description} {collection.description}</p>
            <span className="home-showcase-cue">{active.cue}</span>
            {onOpenTemplate ? <button className="text-button home-showcase-cta" type="button" onClick={() => onOpenTemplate(active.id, activeCollection)}>用这种方式开始 <ArrowRight size={17} /></button> : null}
          </div>
        </div>
        <div className="home-showcase-stage" id="home-showcase-preview" role="region" aria-live="polite" aria-label={`${active.name}预览`}>
          <div className="home-showcase-stage-label"><span>LIVE PREVIEW</span><span>{String(SHOWCASE_ENTRIES.indexOf(active) + 1).padStart(2, "0")} / {String(SHOWCASE_ENTRIES.length).padStart(2, "0")}</span></div>
          <AlbumPreview key={`${activeTemplate}-${activeCollection}`} document={previewDocument} urls={SHOWCASE_URLS} />
        </div>
      </Reveal>
    </section>
  );
}
